import { Footer } from '../Common/Footer';
import { Button, Divider, DrawerBody, makeStyles, OverlayDrawer, shorthands, Text, tokens, Tooltip } from '@fluentui/react-components';
import { LayoutGrid } from 'lucide-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import Skeleton from 'react-loading-skeleton';
import { useRootState } from '../../store/hooks';
import { useTranslation } from 'react-i18next';
import { ActionTypes, AttachmentSourceType, GptTypes, IConversation, ISource } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useIsMobile } from '../../Hooks/useResponsive';
import { useConversations } from '../../store/hooks';
import { updateActiveConversationAction } from '../../store/actions/conversationActions';
// getGptIconAsset removed because we no longer show the GPT logo
import { FeatureHighlightPopup } from '../Common/FeatureHighlightPopup';
import SourcePreview, { SourcePreviewRef } from '../SourcePreview/SourcePreview';
import DrawerHeaderTitleCustom from '../Common/DrawerHeaderTitleCustom';
import { useNotification } from '../../Contexts/NotificationContext';
import './ConversationView.scss';
import { PromptList } from './PromptList';
import StopCompletionButton from './StopCompletionButton';
import { UserQuery } from './UserQuery';
import { WelcomeBox } from './WelcomeBox';
import { ConversationHeaderMenu } from './ConversationHeaderMenu';

const styles = makeStyles({
    header: {
        height: '24px',
        minHeight: '40px',
        maxHeight: '40px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        ...shorthands.padding('10px', '16px'),
        // Reduce header padding on mobile
        '@media (max-width: 768px)': {
            paddingTop: '10px',
            paddingRight: '16px',
            paddingBottom: '0px',
            paddingLeft: '16px',
        },
    },
    conversationTitleHeader: {
        fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        letterSpacing: '0.09px',
        color: tokens.colorNeutralForeground1,
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
        minWidth: 0,
        '@media (max-width: 768px)': {
            fontSize: '18px',
        },
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
        minWidth: 0,
    },
    gptTagHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap('8px'),
        padding: 0,
        backgroundColor: 'transparent',
        borderRadius: 0,
        // allow shrinking so the badge label can truncate inside flex container
        flex: '0 1 auto',
    },
});

// represents the conversation view, which contains the list of messages, the user query input, the welcome box and the source preview panel (if enabled by the user, e.g. for RAG tools)
export interface ConversationProps {
    canSubmit: boolean;
    data: IConversation;
    isUploadAttachmentOver: boolean;
    onUpdate: (actionType: ActionTypes, updatedPromptIndex: number, data: IConversation) => void;
    onSendQuery: (query: string, plugins: string[], modelName: string) => boolean | Promise<boolean>;
    onDocumentUpload: (files: FileList, source: AttachmentSourceType) => void;
    onDocumentRemove: (filename: string) => void;
    onUpdateAnsweredQuery: (updatedPromptIndex: number, newQuery: string) => void;
    completionInProgress: boolean;
    onStopCompletion: () => void;
    restoreLastQueryTrigger?: number;
    restoreReason?: 'error' | 'stop';
    onTitleChange?: (id: string, title: string) => void;
    onRemoveConversation?: (id: string) => void;
    onPinToggle?: (id: string, pinned: boolean) => void;
    isPinned?: boolean;
}

export const ConversationView: FC<ConversationProps> = (props: ConversationProps) => {
    const classes = styles();
    const { canSubmit, completionInProgress, data, isUploadAttachmentOver, onUpdate, onSendQuery, onDocumentUpload, onDocumentRemove, onStopCompletion, onUpdateAnsweredQuery, restoreLastQueryTrigger, restoreReason, onTitleChange, onRemoveConversation, onPinToggle, isPinned } = props;
    const { activeGpt, dispatch } = useConversations();
    const [sourcePreview, setSourcePreview] = useState<ISource | undefined>(undefined);
    const [starterToggle, setStarterToggle] = useState<boolean>(false);
    const starterRef = useRef<string>('');
    const { t } = useTranslation();
    const [userInput, setUserInput] = useState<string>(''); // Add state for user input
    const [clearEditorTrigger, setClearEditorTrigger] = useState<number>(0); // Trigger to clear editor
    const initialConversationId = useRef<string | undefined>(undefined);
    const [isEditingPrompt, setIsEditingPrompt] = useState<boolean>(false);

    const handleSelectConversationStarter = (starter: string) => {
        starterRef.current = starter;
        // Toggle boolean to force update even if same starter is selected
        setStarterToggle((prev) => !prev);
    };    const getStarter = useCallback(() => {
        return starterRef.current;
    }, [starterToggle]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const previousId = initialConversationId.current;
        
        // Only clear user input when truly switching between different conversations
        // Preserve input when:
        // - First load (previousId is undefined)
        // - Staying in same conversation (same ID)
        // - Any transition involving DRAFT (DRAFT -> conversation, conversation -> DRAFT, DRAFT -> DRAFT)
        const shouldClearInput = previousId !== undefined && 
                                 previousId !== data.id && 
                                 previousId !== 'DRAFT' && 
                                 data.id !== 'DRAFT';
        
        if (shouldClearInput) {
            starterRef.current = '';
            setUserInput('');
            setClearEditorTrigger(prev => prev + 1);
        } else if (data.currentQuery && previousId !== data.id) {
            // Restore currentQuery when transitioning from DRAFT to a real conversation
            // This happens when uploading a file creates the conversation
            setUserInput(data.currentQuery);
        }
        
        // Always update the reference to track the current conversation
        initialConversationId.current = data.id;
    }, [data.id, data.currentQuery]);

    // Wrapper for onDocumentUpload that saves userInput to currentQuery before upload
    const handleDocumentUpload = useCallback((files: FileList, source: AttachmentSourceType) => {
        // Save current user input to conversation's currentQuery before upload
        if (userInput && userInput.trim()) {
            dispatch(updateActiveConversationAction({
                id: data.id,
                conversation: { ...data, currentQuery: userInput }
            }));
        }
        // Call the original upload handler
        onDocumentUpload(files, source);
    }, [userInput, data, dispatch, onDocumentUpload]);

    const isMobile = useIsMobile();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { dispatchNotification } = useNotification();
    const { state } = useRootState();
    const appLoading = state.appLoading ?? false;
    const conversationsLoading = state.conversationsLoading ?? false;
    const loading = appLoading || conversationsLoading;

    const isWelcome = !(data.prompts && data.prompts.length > 0);
    const sourcePreviewRef = useRef<SourcePreviewRef | null>(null);
    // detect presence of conversation id parameter in the URL (route uses { id })
    const params = useParams() as { id?: string };
    const hasConversationId = !!(params && params.id);

    if (loading) {
        // If a conversation is being loaded (conversation id in URL), show a chat-style skeleton
            if (hasConversationId) {
            return (
                <div className={`conversationView ${isMobile ? 'mobile' : ''}`}>
                    <div className="skeleton-loading-wrapper">
                        <div className="skeleton-content">
                        {/* Simulate a sequence using the same markup as real messages so skeletons align perfectly */}
                        <div className='queryMessage skeleton-only'>
                            <Skeleton className="skeleton-253x83" />
                        </div>

                        <div className='answerMessage skeleton-only'>
                            <Skeleton className="skeleton-253x32" />
                        </div>

                        <div className='queryMessage skeleton-only'>
                            <Skeleton className="skeleton-253x83" />
                        </div>

                        <div className='answerMessage skeleton-only'>
                            <Skeleton className="skeleton-253x32" />
                        </div>
                        <div className="skeleton-rect-wrapper">
                            <Skeleton className="skeleton-800x173" height={173} />
                        </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Otherwise (no conversation id) show three generic centered rectangle placeholders
        // widths are controlled via CSS (ConversationView.scss) for desktop and mobile
        return (
            <div className={`conversationView ${isMobile ? 'mobile' : ''}`}>
                <div style={{ padding: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
                    <div className="skeleton-rect-wrapper">
                        <Skeleton className="skeleton-170x53" height={53} />
                    </div>
                    <div className="skeleton-rect-wrapper">
                        <Skeleton className="skeleton-436x41" height={41} />
                    </div>
                    <div className="skeleton-rect-wrapper">
                        <Skeleton className="skeleton-800x173" height={173} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`conversationView${isWelcome ? ' show-welcome' : ''} ${isMobile ? 'mobile' : ''}`}>
            {!isWelcome && (
                <div>
                    <div className={classes.header}>
                        <div className={classes.headerLeft}>
                            {
                                // Show GPT badge when this conversation is backed by a GPT
                                data.gpt && activeGpt && (
                                    <div className={classes.gptTagHeader} aria-hidden={false}>
                                        {
                                            activeGpt?.isDefault ? <></> : (
                                                <div className='gpt-badge' role='presentation'>
                                                    <div className='gpt-badge__icon' aria-hidden>
                                                        <LayoutGrid size={18} />
                                                    </div>
                                                    <div className='gpt-badge__label'>
                                                        <Text weight='semibold' className='gpt-name gpt-badge__text'>
                                                            {activeGpt?.name ?? ''}
                                                        </Text>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                )
                            }
                            {data.prompts && data.prompts.length > 0 && data.title && (
                                <Text className={classes.conversationTitleHeader}>{data.title}</Text>
                            )}
                        </div>
                        <div className={'conversationHeaderButtons'}>
                            {onTitleChange && onRemoveConversation && onPinToggle && (
                                <ConversationHeaderMenu
                                    conversationId={data.id}
                                    conversationTitle={data.title || ''}
                                    isPinned={isPinned || false}
                                    onTitleChange={onTitleChange}
                                    onRemove={onRemoveConversation}
                                    onPinToggle={onPinToggle}
                                />
                            )}
                            {/* <Tooltip content={'Suggest an idea'} relationship='label'>
                                <Button
                                    shape='circular'
                                    appearance='primary'
                                    icon={<LightbulbFilled />}
                                    onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSez-Y66Wac_o13XxBEPTAqOw1dYMHDMlkJDQLfi0Ko-4YjbcA/viewform?usp=dialog', '_blank')}
                                ></Button>
                            </Tooltip> */}
                            {/* <Tooltip content={'Contact us'} relationship='label'>
                                <a href='mailto:syensqo.ai@syensqo.com'>
                                    <Button shape='circular' appearance='primary' icon={<MailFilled />}></Button>
                                </a>
                            </Tooltip> 
                            <Tooltip content={'Open Wiki for help'} relationship='label'>
                                <Button
                                    shape='circular'
                                    appearance='primary'
                                    icon={<QuestionCircleFilled />}
                                    onClick={() => window.open('https://wiki.syensqo.com/display/IC/SyGPT+Home', '_blank')}
                                ></Button>
                            </Tooltip>*/}
                        </div>
                    </div>
                    <Divider />
                </div>
            )}
            {
                // shows the list of messages if there are any for the specific conversation, otherwise shows the welcome box
                data.prompts && data.prompts.length > 0 ? (
                    <>
                        <PromptList
                            key={`promptList_${data.id}`}
                            prompts={data.prompts ?? []}
                            conversationTitle={data.title}
                            onUpdate={(actionType, prompts, updatedPromptIndex) => {
                                onUpdate(actionType, updatedPromptIndex, { ...data, prompts });
                            }}
                            onUpdateAnsweredQuery={onUpdateAnsweredQuery}
                            onSourceClick={(source) => setSourcePreview(source)}
                            onEditingChange={setIsEditingPrompt}
                        />
                    </>
                ) : (
                    <>
                        <div className="welcome-section">
                            <FeatureHighlightPopup
                                id={'mainHighLights'}
                                header={t('featureHighlights.header')}
                                title={t('featureHighlights.mainHighLights.title')}
                                content={t('featureHighlights.mainHighLights.content')}
                                link={t('featureHighlights.mainHighLights.link')}
                            >
                                <WelcomeBox key={`welcomeBox${data.id}`} />
                            </FeatureHighlightPopup>
                        </div>
                    </>
                )
            }
            {/* If there is no prompt, show the suggested prompts */}
            <StopCompletionButton completionInProgress={completionInProgress} onStopCompletion={onStopCompletion} />
            <UserQuery
                key={`userquery_${data.id}`}
                isUploadAttachmentOver={isUploadAttachmentOver}
                conversationStarterQuery={getStarter}
                canSubmit={canSubmit}
                completionInProgress={completionInProgress}
                onSendQuery={onSendQuery}
                onUploadDocumentAdd={handleDocumentUpload}
                onUploadDocumentRemove={onDocumentRemove}
                hideModelSelection={data.gpt?.type === GptTypes.SyensqoInformationGpt}
                restoreLastQueryTrigger={restoreLastQueryTrigger}
                inputValue={userInput} // Pass input value
                onInputChange={setUserInput} // Pass setter
                clearEditorTrigger={clearEditorTrigger} // Pass clear trigger
                restoreReason={restoreReason}
                isEditingPrompt={isEditingPrompt}
            />
            <Footer />
            <OverlayDrawer open={!!sourcePreview} position='end' size='medium'>
                <DrawerHeaderTitleCustom
                    title={sourcePreview ? (sourcePreview.webSiteUrl ? sourcePreview.webSiteUrl : sourcePreview.title) : ''}
                    isLoading={isLoading}
                    openLabel={sourcePreview && sourcePreview.webSiteUrl ? 'Open website' : 'Open document'}
                    onOpen={() => {
                        if (!sourcePreview) return;
                        // delegate the open action to the SourcePreview component
                        (async () => {
                            try {
                                setIsLoading(true);
                                await sourcePreviewRef.current?.openSource();
                            } catch (e: unknown) {
                                dispatchNotification('error.api.get.document', 'error');
                            } finally {
                                setIsLoading(false);
                            }
                        })();
                    }}
                    onClose={() => setSourcePreview(undefined)}
                />
                <DrawerBody>{sourcePreview && <SourcePreview ref={sourcePreviewRef} source={sourcePreview} conversationId={data.id}></SourcePreview>}</DrawerBody>
            </OverlayDrawer>
        </div>
    );
};
